import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { AlertTriangle, RefreshCw, Loader2, ShieldAlert, Check, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const SEVERITY_STYLE = {
  high:   { chip: "bg-rose-100 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  medium: { chip: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  low:    { chip: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
};

export default function AdminAnomalies() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState({ status: "open", severity: null });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.severity) params.severity = filter.severity;
      const [list, sum] = await Promise.all([
        api.get("/admin/anomalies/", { params }),
        api.get("/admin/anomalies/summary"),
      ]);
      setRows(list.data.anomalies || []);
      setSummary(sum.data);
    } catch (e) {
      toast.error("Failed to load anomalies");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.severity]);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/anomalies/scan", null, { params: { window_hours: 24 } });
      toast.success(`Scan complete · ${data.total} flag${data.total === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error("Scan failed"); }
    finally { setScanning(false); }
  };

  const review = async (id, status) => {
    try {
      await api.post(`/admin/anomalies/${id}/review`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="admin-anomalies-page">
      <div className="flex items-center justify-between gap-3 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="h-7 w-7 text-rose-500" /> Anomaly Detection
          </h1>
          <p className="text-sm text-slate-500 mt-1">Rule-based flags for human review. Never auto-blocks.</p>
        </div>
        <Button onClick={runScan} disabled={scanning} className="bg-[#FF6B00] hover:bg-orange-600 gap-2" data-testid="anomalies-scan-btn">
          {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Run scan now
        </Button>
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fadeInUp">
          <Tile label="Open" value={summary.by_status.open || 0} color="text-rose-700" />
          <Tile label="Reviewed" value={summary.by_status.reviewed || 0} color="text-emerald-700" />
          <Tile label="High severity" value={summary.by_severity.high || 0} color="text-rose-700" />
          <Tile label="Last scan" value={summary.last_run ? new Date(summary.last_run.ts).toLocaleString() : "—"} color="text-slate-700" small />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 animate-fadeInUp" data-testid="anomalies-filters">
        {["open", "reviewed", "dismissed", null].map((s) => (
          <button
            key={s ?? "all"}
            onClick={() => setFilter((f) => ({ ...f, status: s }))}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${filter.status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
            data-testid={`filter-status-${s || "all"}`}
          >
            {s ?? "All"}
          </button>
        ))}
        <span className="mx-2 text-slate-300">·</span>
        {["high", "medium", "low", null].map((s) => (
          <button
            key={s ?? "all-sev"}
            onClick={() => setFilter((f) => ({ ...f, severity: s }))}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${filter.severity === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
            data-testid={`filter-sev-${s || "all"}`}
          >
            {s ?? "All severity"}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="anomalies-list">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" /></div>
        ) : !rows.length ? (
          <div className="p-12 text-center">
            <AlertTriangle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">All clear</p>
            <p className="text-sm text-slate-400 mt-1">No anomalies for the current filters.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {rows.map((a) => {
              const sty = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.low;
              return (
                <li key={a.id} className="p-4 sm:p-5 flex items-start gap-4" data-testid={`anomaly-row-${a.id}`}>
                  <span className={`w-2 h-2 rounded-full mt-2 ${sty.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${sty.chip}`}>{a.severity}</span>
                      <span className="text-xs font-mono text-slate-400">{a.rule_id}</span>
                      <span className="text-[10px] uppercase tracking-widest text-slate-400">→ {a.entity_type}</span>
                      <span className="font-mono text-xs text-slate-600 truncate">{a.entity_id}</span>
                    </div>
                    <p className="text-sm text-slate-900 mt-1.5 font-medium">{a.reason}</p>
                    <pre className="mt-2 text-[11px] bg-slate-50 rounded-lg p-2 text-slate-600 overflow-x-auto">{JSON.stringify(a.evidence, null, 0)}</pre>
                    <p className="text-[10px] text-slate-400 mt-1">{new Date(a.created_at).toLocaleString()}</p>
                  </div>
                  {a.status === "open" && (
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => review(a.id, "reviewed")} data-testid={`anomaly-review-${a.id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Reviewed
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => review(a.id, "dismissed")} data-testid={`anomaly-dismiss-${a.id}`}>
                        <X className="h-3.5 w-3.5 mr-1" /> Dismiss
                      </Button>
                    </div>
                  )}
                  {a.status !== "open" && (
                    <span className="text-xs text-slate-400 shrink-0 self-center capitalize">
                      {a.status} <ChevronRight className="h-3 w-3 inline" />
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, color = "text-slate-700", small }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p className={`mt-1.5 font-['Outfit'] font-bold ${color} ${small ? "text-sm" : "text-2xl"}`}>{value}</p>
    </div>
  );
}
